import React, { lazy, Suspense, useEffect, useState } from 'react';
import { MarketingLayout } from './MarketingLayout';
import { HOME_META, SITE_URL } from './siteConfig';
import { useSeo, setJsonLd } from './seo';
import { Hero } from './sections/Hero';
import { TrustStrip } from './sections/TrustStrip';
import { Features } from './sections/Features';
import { SampleOutlook } from './sections/SampleOutlook';
import { HowItWorks } from './sections/HowItWorks';
import { InsideWorkspace } from './sections/InsideWorkspace';
import { NumbersBuilt } from './sections/NumbersBuilt';
import { TrackRecordTeaser } from './sections/TrackRecordTeaser';
import { PricingPreview } from './sections/PricingPreview';
import { Faq } from './sections/Faq';
import { FinalCta } from './sections/FinalCta';
import { FAQS } from './faqs';

const AuthModal = lazy(() => import('./AuthModal'));

const useHomeStructuredData = () => useEffect(() => {
  setJsonLd('ld-faq', {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  });
  setJsonLd('ld-webpage', { '@context': 'https://schema.org', '@type': 'WebPage', name: HOME_META.title, url: `${SITE_URL}/`, description: HOME_META.description });
}, []);

// useAuth is passed when rendered inside the full App; otherwise onRequestApp loads the App bundle for sign-in.
export default function HomePage({ useAuth, onRequestApp }) {
  const [auth, setAuth] = useState(null);
  useSeo({ ...HOME_META, path: '/' });
  useHomeStructuredData();

  useEffect(() => {
    const mode = new URLSearchParams(window.location.search).get('auth');
    if (useAuth && (mode === 'signup' || mode === 'login')) setAuth(mode);
    const id = window.location.hash.slice(1);
    if (id && !id.includes('=')) setTimeout(() => document.getElementById(id)?.scrollIntoView(), 60);
  }, []);

  const openAuth = (mode) => (useAuth ? setAuth(mode) : onRequestApp(mode));

  return (
    <MarketingLayout isHome onAuth={openAuth}>
      <Hero onAuth={openAuth} />
      <TrustStrip />
      {/* SOCIAL PROOF SLOT: add testimonials, user counts, press logos or awards here only once they are real and verifiable. */}
      <Features />
      <SampleOutlook />
      <HowItWorks />
      <InsideWorkspace />
      <NumbersBuilt />
      <TrackRecordTeaser />
      <PricingPreview onAuth={openAuth} />
      <Faq />
      <FinalCta onAuth={openAuth} />
      {auth && (
        <Suspense fallback={null}>
          <AuthModal mode={auth} useAuth={useAuth} onClose={() => setAuth(null)} />
        </Suspense>
      )}
    </MarketingLayout>
  );
}
